// HKDSE 2021 Paper 1 Knowledge Graph Samples - Part 2
// More complex questions and advanced topics

export const hkdse2021Samples_part2 = {
  geometry: [
    {
      id: 'hkdse2021_geometry_01',
      problem: "In Figure 1, $ABCD$ is a quadrilateral where the extension of $AB$ and the extension of $CD$ intersect at point $E$. Given that $\\angle CAE = \\angle BDE$. (a) Prove that $\\triangle ACE \\sim \\triangle DBE$. (b) Given that $AC = 25$ cm, $AE = 60$ cm, $CE = 65$ cm, and $BD = 15$ cm: (i) Is $\\triangle ACE$ a right-angled triangle? Explain your answer. (ii) Find the area of $\\triangle BDE$.",
      problemType: "geometry_similarity",
      difficulty: "harder",
      description: "Similar triangles and geometric properties",
      graphData: {
        nodes: [
          {
            id: "quadrilateral_intersection",
            name: "Quadrilateral with Intersecting Extensions",
            type: "concepts",
            description: "ABCD with AB and CD extended meeting at E",
            value: "AB extended meets CD extended at E",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_angles",
            name: "Given Angles",
            type: "concepts",
            description: "∠CAE = ∠BDE",
            value: "∠CAE = ∠BDE",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "similarity_criteria",
            name: "Similarity Criteria",
            type: "concepts",
            description: "AA similarity criterion",
            value: "Two angles equal implies similar triangles",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_identify_angles",
            name: "Step 1: Identify Equal Angles",
            type: "steps",
            description: "∠CAE = ∠BDE (given)",
            value: "∠CAE = ∠BDE",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_vertical_angles",
            name: "Step 2: Vertical Angles",
            type: "steps",
            description: "∠AEC = ∠DEB (vertical angles)",
            value: "∠AEC = ∠DEB",
            importance: 8,
            x: 0, y: 50, z: 0
          },
          {
            id: "step3_aa_similarity",
            name: "Step 3: AA Similarity",
            type: "steps",
            description: "Two pairs of equal angles",
            value: "△ACE ∼ △DBE by AA",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_pythagoras",
            name: "Part (b): Check Right Angle",
            type: "steps",
            description: "Check if AC² + CE² = AE²",
            value: "25² + 65² = 625 + 4225 = 4850 ≠ 60² = 3600",
            importance: 9,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_b_not_right",
            name: "Not Right-Angled",
            type: "values",
            description: "Triangle is not right-angled",
            value: "△ACE is not right-angled",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_b_similarity_ratio",
            name: "Part (b): Similarity Ratio",
            type: "steps",
            description: "Find ratio of corresponding sides",
            value: "AC/DB = 25/15 = 5/3",
            importance: 8,
            x: 200, y: 150, z: 0
          },
          {
            id: "part_b_area_ratio",
            name: "Part (b): Area Ratio",
            type: "steps",
            description: "Area ratio is square of similarity ratio",
            value: "Area ratio = (5/3)² = 25/9",
            importance: 9,
            x: 200, y: 200, z: 0
          },
          {
            id: "part_b_find_area_ace",
            name: "Part (b): Find Area ACE",
            type: "steps",
            description: "Use Heron's formula or base × height",
            value: "Area ACE = ½ × AC × CE × sin(angle)",
            importance: 8,
            x: 200, y: 250, z: 0
          },
          {
            id: "final_answer_area",
            name: "Final Answer: Area BDE",
            type: "values",
            description: "Area of triangle BDE",
            value: "Area BDE = (9/25) × Area ACE",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "quadrilateral_intersection", to: "given_angles", type: "enables", strength: 9 },
          { from: "given_angles", to: "similarity_criteria", type: "enables", strength: 9 },
          { from: "similarity_criteria", to: "step1_identify_angles", type: "enables", strength: 8 },
          { from: "step1_identify_angles", to: "step2_vertical_angles", type: "enables", strength: 8 },
          { from: "step2_vertical_angles", to: "step3_aa_similarity", type: "leads to", strength: 9 },
          { from: "step3_aa_similarity", to: "part_b_pythagoras", type: "enables", strength: 8 },
          { from: "part_b_pythagoras", to: "part_b_not_right", type: "leads to", strength: 8 },
          { from: "step3_aa_similarity", to: "part_b_similarity_ratio", type: "enables", strength: 8 },
          { from: "part_b_similarity_ratio", to: "part_b_area_ratio", type: "leads to", strength: 9 },
          { from: "part_b_area_ratio", to: "part_b_find_area_ace", type: "enables", strength: 8 },
          { from: "part_b_find_area_ace", to: "final_answer_area", type: "leads to", strength: 10 }
        ]
      }
    }
  ],
  
  statistics: [
    {
      id: 'hkdse2021_statistics_01',
      problem: "The following bar chart shows the distribution of the number of books read by a group of students in a certain year. If a student is randomly selected from this group, the probability that the selected student read fewer than 26 books in that year is $\\frac{7}{10}$. (a) Find $k$. (b) Find the range, interquartile range, and standard deviation of this distribution.",
      problemType: "statistics_distribution",
      difficulty: "intermediate",
      description: "Statistical analysis of grouped data",
      graphData: {
        nodes: [
          {
            id: "bar_chart_data",
            name: "Bar Chart Data",
            type: "concepts",
            description: "Distribution of books read by students",
            value: "23 books (12), 24 books (k), 25 books (16), 26 books (9), 27 books (11), 28 books (4)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "probability_condition",
            name: "Probability Condition",
            type: "concepts",
            description: "P(fewer than 26 books) = 7/10",
            value: "P(< 26) = 7/10",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_total",
            name: "Step 1: Find Total Students",
            type: "steps",
            description: "Calculate total number of students",
            value: "Total = 12 + k + 16 + 9 + 11 + 4 = k + 52",
            importance: 8,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_find_fewer_26",
            name: "Step 2: Find Students with < 26 books",
            type: "steps",
            description: "Students with 23, 24, or 25 books",
            value: "Students with < 26 = 12 + k + 16 = k + 28",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_setup_equation",
            name: "Step 3: Setup Probability Equation",
            type: "steps",
            description: "Set up the probability equation",
            value: "(k + 28)/(k + 52) = 7/10",
            importance: 9,
            x: 0, y: 50, z: 0
          },
          {
            id: "step4_solve_for_k",
            name: "Step 4: Solve for k",
            type: "steps",
            description: "Cross multiply and solve",
            value: "10(k + 28) = 7(k + 52) → 10k + 280 = 7k + 364 → 3k = 84 → k = 28",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_range",
            name: "Part (b): Range",
            type: "steps",
            description: "Range = maximum - minimum",
            value: "Range = 28 - 23 = 5",
            importance: 8,
            x: -200, y: 150, z: 0
          },
          {
            id: "part_b_median",
            name: "Part (b): Find Median",
            type: "steps",
            description: "Find the median position",
            value: "Total students = 80, median position = 40.5",
            importance: 8,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_b_quartiles",
            name: "Part (b): Find Quartiles",
            type: "steps",
            description: "Find Q1 and Q3 positions",
            value: "Q1 position = 20.25, Q3 position = 60.75",
            importance: 8,
            x: 200, y: 150, z: 0
          },
          {
            id: "part_b_iqr",
            name: "Part (b): Interquartile Range",
            type: "steps",
            description: "IQR = Q3 - Q1",
            value: "IQR = Q3 - Q1",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_standard_deviation",
            name: "Part (b): Standard Deviation",
            type: "steps",
            description: "Calculate standard deviation",
            value: "σ = √[Σ(x - μ)²f / Σf]",
            importance: 9,
            x: 0, y: 250, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All statistical measures",
            value: "k = 28, Range = 5, IQR = Q3 - Q1, σ = calculated value",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "bar_chart_data", to: "probability_condition", type: "enables", strength: 9 },
          { from: "probability_condition", to: "step1_find_total", type: "enables", strength: 8 },
          { from: "step1_find_total", to: "step2_find_fewer_26", type: "enables", strength: 8 },
          { from: "step2_find_fewer_26", to: "step3_setup_equation", type: "leads to", strength: 9 },
          { from: "step3_setup_equation", to: "step4_solve_for_k", type: "leads to", strength: 9 },
          { from: "step4_solve_for_k", to: "part_b_range", type: "enables", strength: 8 },
          { from: "step4_solve_for_k", to: "part_b_median", type: "enables", strength: 8 },
          { from: "part_b_median", to: "part_b_quartiles", type: "enables", strength: 8 },
          { from: "part_b_quartiles", to: "part_b_iqr", type: "leads to", strength: 8 },
          { from: "step4_solve_for_k", to: "part_b_standard_deviation", type: "enables", strength: 9 },
          { from: "part_b_iqr", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_standard_deviation", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ],
  
  functions: [
    {
      id: 'hkdse2021_functions_01',
      problem: "It is known that part of $f(x)$ is a constant, and another part varies as $(x+4)^2$. Given that $f(-3) = 0$ and $f(2) = 105$. (a) Find $f(0)$. (b) Let the graph of $y = f(x) + 3$ be denoted as $G$. (i) Find the y-intercept of $G$. (ii) Find the x-intercept of $G$.",
      problemType: "functions_variation",
      difficulty: "harder",
      description: "Function with constant and variable parts",
      graphData: {
        nodes: [
          {
            id: "function_structure",
            name: "Function Structure",
            type: "concepts",
            description: "f(x) has constant and variable parts",
            value: "f(x) = c + k(x+4)²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_conditions",
            name: "Given Conditions",
            type: "concepts",
            description: "Two points on the function",
            value: "f(-3) = 0, f(2) = 105",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_setup_equations",
            name: "Step 1: Setup Equations",
            type: "steps",
            description: "Substitute given points into function",
            value: "f(-3) = c + k(-3+4)² = c + k = 0",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1b_second_equation",
            name: "Step 1b: Second Equation",
            type: "steps",
            description: "Second equation from f(2) = 105",
            value: "f(2) = c + k(2+4)² = c + 36k = 105",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_solve_system",
            name: "Step 2: Solve System",
            type: "steps",
            description: "Solve the system of equations",
            value: "c + k = 0, c + 36k = 105",
            importance: 9,
            x: 0, y: 50, z: 0
          },
          {
            id: "step2a_subtract_equations",
            name: "Step 2a: Subtract Equations",
            type: "steps",
            description: "Subtract first equation from second",
            value: "35k = 105 → k = 3",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "step2b_find_c",
            name: "Step 2b: Find c",
            type: "steps",
            description: "Substitute k back to find c",
            value: "c + 3 = 0 → c = -3",
            importance: 8,
            x: 0, y: 150, z: 0
          },
          {
            id: "step3_write_function",
            name: "Step 3: Write Function",
            type: "steps",
            description: "Write the complete function",
            value: "f(x) = -3 + 3(x+4)²",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_a_find_f0",
            name: "Part (a): Find f(0)",
            type: "steps",
            description: "Substitute x = 0",
            value: "f(0) = -3 + 3(0+4)² = -3 + 48 = 45",
            importance: 9,
            x: 0, y: 250, z: 0
          },
          {
            id: "part_b_graph_g",
            name: "Part (b): Graph G",
            type: "steps",
            description: "G is f(x) + 3",
            value: "G: y = f(x) + 3 = -3 + 3(x+4)² + 3 = 3(x+4)²",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_b_y_intercept",
            name: "Part (b): Y-Intercept",
            type: "steps",
            description: "Find y-intercept of G",
            value: "When x = 0: y = 3(0+4)² = 48",
            importance: 8,
            x: -200, y: 250, z: 0
          },
          {
            id: "part_b_x_intercept",
            name: "Part (b): X-Intercept",
            type: "steps",
            description: "Find x-intercept of G",
            value: "When y = 0: 3(x+4)² = 0 → x = -4",
            importance: 8,
            x: 200, y: 250, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required values",
            value: "f(0) = 45, y-intercept = 48, x-intercept = -4",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "function_structure", to: "given_conditions", type: "enables", strength: 9 },
          { from: "given_conditions", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "step1_setup_equations", to: "step1b_second_equation", type: "enables", strength: 9 },
          { from: "step1b_second_equation", to: "step2_solve_system", type: "leads to", strength: 9 },
          { from: "step2_solve_system", to: "step2a_subtract_equations", type: "leads to", strength: 8 },
          { from: "step2a_subtract_equations", to: "step2b_find_c", type: "leads to", strength: 8 },
          { from: "step2b_find_c", to: "step3_write_function", type: "leads to", strength: 9 },
          { from: "step3_write_function", to: "part_a_find_f0", type: "enables", strength: 9 },
          { from: "step3_write_function", to: "part_b_graph_g", type: "enables", strength: 8 },
          { from: "part_b_graph_g", to: "part_b_y_intercept", type: "enables", strength: 8 },
          { from: "part_b_graph_g", to: "part_b_x_intercept", type: "enables", strength: 8 },
          { from: "part_a_find_f0", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_y_intercept", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_x_intercept", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    },
    {
      id: 'hkdse2021_statistics_02',
      problem: "The following table shows the distribution of the number of tokens won by a group of children in a game. (a) Find the mean of this distribution. (b) Are the median and mode of this distribution equal? Explain your answer. (c) If $n$ more children participate in the game and each of them wins 5 tokens, find: (i) The value of $n$ such that the mean of the distribution increases by 1; (ii) The minimum value of $n$ such that the median of the distribution increases by 2; (iii) The maximum value of $n$ such that the mode of the distribution remains unchanged.",
      problemType: "statistics_distribution_analysis",
      difficulty: "harder",
      description: "Advanced statistics with distribution analysis",
      graphData: {
        nodes: [
          {
            id: "frequency_table",
            name: "Frequency Table",
            type: "concepts",
            description: "Tokens: 1,2,3,4,5,6,7; Frequency: 15,9,2,5,4,2,5",
            value: "Total children = 42",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "part_a_mean",
            name: "Part (a): Mean",
            type: "steps",
            description: "Calculate mean using Σfx/Σf",
            value: "Mean = (1×15 + 2×9 + 3×2 + 4×5 + 5×4 + 6×2 + 7×5)/42 = 3.31",
            importance: 8,
            x: 0, y: -200, z: 0
          },
          {
            id: "part_b_median",
            name: "Part (b): Median",
            type: "steps",
            description: "Find median position and value",
            value: "Median position = 21st value = 2",
            importance: 8,
            x: -150, y: -100, z: 0
          },
          {
            id: "part_b_mode",
            name: "Part (b): Mode",
            type: "steps",
            description: "Find most frequent value",
            value: "Mode = 1 (frequency 15)",
            importance: 8,
            x: 150, y: -100, z: 0
          },
          {
            id: "part_b_comparison",
            name: "Part (b): Comparison",
            type: "steps",
            description: "Compare median and mode",
            value: "Median = 2, Mode = 1, so they are not equal",
            importance: 9,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_c_new_mean",
            name: "Part (c): New Mean",
            type: "steps",
            description: "New mean = old mean + 1 = 4.31",
            value: "New mean = 3.31 + 1 = 4.31",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "part_c_mean_equation",
            name: "Part (c): Mean Equation",
            type: "steps",
            description: "Set up equation for new mean",
            value: "(139 + 5n)/(42 + n) = 4.31",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_c_solve_n",
            name: "Part (c): Solve for n",
            type: "steps",
            description: "Solve the equation",
            value: "n = 8",
            importance: 9,
            x: 200, y: 100, z: 0
          },
          {
            id: "part_c_median_increase",
            name: "Part (c): Median Increase",
            type: "steps",
            description: "New median = 4, find minimum n",
            value: "Need n ≥ 9 for median to become 4",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_c_mode_unchanged",
            name: "Part (c): Mode Unchanged",
            type: "steps",
            description: "Mode remains 1, find maximum n",
            value: "Maximum n = 14",
            importance: 8,
            x: 200, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required values",
            value: "(a) 3.31 (b) No (c) (i) 8 (ii) 9 (iii) 14",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "frequency_table", to: "part_a_mean", type: "enables", strength: 9 },
          { from: "frequency_table", to: "part_b_median", type: "enables", strength: 8 },
          { from: "frequency_table", to: "part_b_mode", type: "enables", strength: 8 },
          { from: "part_b_median", to: "part_b_comparison", type: "enables", strength: 9 },
          { from: "part_b_mode", to: "part_b_comparison", type: "enables", strength: 9 },
          { from: "part_a_mean", to: "part_c_new_mean", type: "enables", strength: 8 },
          { from: "part_c_new_mean", to: "part_c_mean_equation", type: "enables", strength: 9 },
          { from: "part_c_mean_equation", to: "part_c_solve_n", type: "leads to", strength: 9 },
          { from: "part_c_median_increase", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_mode_unchanged", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_solve_n", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    },
    {
      id: 'hkdse2021_polynomial_01',
      problem: "The polynomial $p(x)$ is divisible by $x-5$. When $p(x)$ is divided by $x^2+x+1$, the quotient and remainder are $2x^2-37$ and $cx+c-1$ respectively, where $c$ is a constant. (a) Find $c$. (b) Prove that $x+3$ is a factor of $p(x)$. (c) Someone claims that all roots of the equation $p(x)=0$ are real numbers. Is this claim correct? Explain your answer.",
      problemType: "polynomial_division_factors",
      difficulty: "harder",
      description: "Advanced polynomial division and factorization",
      graphData: {
        nodes: [
          {
            id: "polynomial_structure",
            name: "Polynomial Structure",
            type: "concepts",
            description: "p(x) = (x²+x+1)(2x²-37) + (cx+c-1)",
            value: "p(x) = 2x⁴ + 2x³ - 35x² - 37x + c(x+1) - 1",
            importance: 10,
            x: -200, y: -200, z: 0
          },
          {
            id: "factor_theorem",
            name: "Factor Theorem",
            type: "concepts",
            description: "If x-5 is a factor, then p(5) = 0",
            value: "p(5) = 0",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "step1_substitute_x5",
            name: "Step 1: Substitute x=5",
            type: "steps",
            description: "Substitute x=5 into p(x)",
            value: "p(5) = 2(5)⁴ + 2(5)³ - 35(5)² - 37(5) + c(5+1) - 1 = 0",
            importance: 9,
            x: 200, y: -200, z: 0
          },
          {
            id: "step2_calculate_p5",
            name: "Step 2: Calculate p(5)",
            type: "steps",
            description: "Calculate the value",
            value: "1250 + 250 - 875 - 185 + 6c - 1 = 439 + 6c = 0",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "step3_solve_c",
            name: "Step 3: Solve for c",
            type: "steps",
            description: "Solve the equation for c",
            value: "6c = -439 → c = -439/6",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_factor_x3",
            name: "Part (b): Factor x+3",
            type: "steps",
            description: "Check if p(-3) = 0",
            value: "Substitute x = -3 into p(x)",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "part_b_calculation",
            name: "Part (b): Calculation",
            type: "steps",
            description: "Calculate p(-3)",
            value: "p(-3) = 162 - 54 - 315 + 111 + c(-2) - 1 = -96 - 2c",
            importance: 8,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_b_result",
            name: "Part (b): Result",
            type: "steps",
            description: "Substitute c value",
            value: "p(-3) = -96 - 2(-439/6) = 0",
            importance: 9,
            x: 200, y: 150, z: 0
          },
          {
            id: "part_c_roots_analysis",
            name: "Part (c): Roots Analysis",
            type: "steps",
            description: "Analyze the nature of roots",
            value: "p(x) has factors (x-5), (x+3), and quadratic factor",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_c_quadratic_roots",
            name: "Part (c): Quadratic Roots",
            type: "steps",
            description: "Check quadratic factor roots",
            value: "x²+x+1 has discriminant < 0, so complex roots",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_c_conclusion",
            name: "Part (c): Conclusion",
            type: "steps",
            description: "Not all roots are real",
            value: "Claim is incorrect - complex roots exist",
            importance: 10,
            x: 200, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) c = -439/6 (b) Proven (c) No, complex roots exist",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "polynomial_structure", to: "step1_substitute_x5", type: "enables", strength: 10 },
          { from: "factor_theorem", to: "step1_substitute_x5", type: "enables", strength: 9 },
          { from: "step1_substitute_x5", to: "step2_calculate_p5", type: "leads to", strength: 10 },
          { from: "step2_calculate_p5", to: "step3_solve_c", type: "leads to", strength: 9 },
          { from: "step3_solve_c", to: "part_b_calculation", type: "enables", strength: 9 },
          { from: "part_b_factor_x3", to: "part_b_calculation", type: "enables", strength: 8 },
          { from: "part_b_calculation", to: "part_b_result", type: "leads to", strength: 9 },
          { from: "part_c_roots_analysis", to: "part_c_quadratic_roots", type: "enables", strength: 9 },
          { from: "part_c_quadratic_roots", to: "part_c_conclusion", type: "leads to", strength: 10 },
          { from: "step3_solve_c", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_result", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_c_conclusion", to: "final_answers", type: "contributes to", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2021_circle_01',
      problem: "The equation of circle $C$ is $x^2+y^2-12x-16y-69=0$. Let $G$ be the center of $C$. Let the origin be denoted as $O$. (a) Find $OG$. (b) Is $O$ inside circle $C$? Explain your answer. (c) Let $P$ be a moving point on the Cartesian plane such that $OP=GP$. Let the locus of $P$ be denoted as $\\Gamma$. Suppose $\\Gamma$ intersects $C$ at points $M$ and $N$. Find the area of quadrilateral $OMGN$.",
      problemType: "circle_geometry_locus",
      difficulty: "harder",
      description: "Advanced circle geometry with locus problems",
      graphData: {
        nodes: [
          {
            id: "circle_equation",
            name: "Circle Equation",
            type: "concepts",
            description: "x² + y² - 12x - 16y - 69 = 0",
            value: "Complete the square to find center and radius",
            importance: 9,
            x: -200, y: -200, z: 0
          },
          {
            id: "completing_square",
            name: "Completing the Square",
            type: "steps",
            description: "Rewrite in standard form",
            value: "(x-6)² + (y-8)² = 121",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "center_radius",
            name: "Center and Radius",
            type: "steps",
            description: "Extract center and radius",
            value: "Center G(6,8), radius = 11",
            importance: 10,
            x: 200, y: -200, z: 0
          },
          {
            id: "part_a_og_distance",
            name: "Part (a): OG Distance",
            type: "steps",
            description: "Find distance from O to G",
            value: "OG = √(6² + 8²) = √100 = 10",
            importance: 8,
            x: 0, y: 0, z: 0
          },
          {
            id: "part_b_inside_check",
            name: "Part (b): Inside Check",
            type: "steps",
            description: "Compare OG with radius",
            value: "OG = 10 < radius = 11, so O is inside",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_c_locus_definition",
            name: "Part (c): Locus Definition",
            type: "concepts",
            description: "Locus of points equidistant from O and G",
            value: "Perpendicular bisector of OG",
            importance: 9,
            x: -200, y: 100, z: 0
          },
          {
            id: "part_c_locus_equation",
            name: "Part (c): Locus Equation",
            type: "steps",
            description: "Find equation of perpendicular bisector",
            value: "y = -3x/4 + 25/2",
            importance: 8,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_c_intersection",
            name: "Part (c): Find Intersection",
            type: "steps",
            description: "Solve system of equations",
            value: "Find points M and N where locus meets circle",
            importance: 8,
            x: 200, y: 150, z: 0
          },
          {
            id: "part_c_area_calculation",
            name: "Part (c): Area Calculation",
            type: "steps",
            description: "Calculate area of quadrilateral",
            value: "Area = ½ × OM × GN × sin(angle)",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) 10 (b) Yes (c) Area = 55",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "circle_equation", to: "completing_square", type: "leads to", strength: 9 },
          { from: "completing_square", to: "center_radius", type: "leads to", strength: 10 },
          { from: "center_radius", to: "part_a_og_distance", type: "enables", strength: 8 },
          { from: "center_radius", to: "part_b_inside_check", type: "enables", strength: 8 },
          { from: "center_radius", to: "part_c_locus_definition", type: "enables", strength: 9 },
          { from: "part_c_locus_definition", to: "part_c_locus_equation", type: "leads to", strength: 8 },
          { from: "part_c_locus_equation", to: "part_c_intersection", type: "leads to", strength: 8 },
          { from: "part_c_intersection", to: "part_c_area_calculation", type: "leads to", strength: 8 },
          { from: "part_a_og_distance", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_b_inside_check", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_area_calculation", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ]
}; 